import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Star, ShieldCheck, CheckCircle2 } from "lucide-react";
import { listPublicReviews, submitReview, type PublicReview } from "@/lib/reviews.functions";

const TITLE = "Omdömen från våra kunder | AK Husbil & Fritid";
const DESCRIPTION =
  "Läs riktiga omdömen från kunder som köpt husbil eller husvagn hos AK Husbil & Fritid i Kalix – och lämna ditt eget. Alla omdömen granskas innan publicering.";

export const Route = createFileRoute("/omdomen")({

  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://www.akhusbilfritid.se/omdomen" }],
  }),
  component: Omdomen,
});

function Stars({ value, size = 16 }: { value: number; size?: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} av 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= value ? "fill-accent text-accent" : "text-muted-foreground/40"}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

function Omdomen() {
  // Omdömen hämtas i bakgrunden så sidan alltid ritas direkt.
  const fetchReviews = useServerFn(listPublicReviews);
  const q = useQuery({
    queryKey: ["public-reviews"],
    queryFn: () => fetchReviews(),
    staleTime: 60_000,
    retry: 1,
  });
  const reviews = (q.data?.reviews ?? []) as PublicReview[];
  const count = q.data?.count ?? 0;
  const average = q.data?.average ?? null;
  const send = useServerFn(submitReview);
  const [rating, setRating] = React.useState(5);
  const [sending, setSending] = React.useState(false);
  const [done, setDone] = React.useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setSending(true);
    try {
      await send({
        data: {
          name: String(fd.get("name") || ""),
          email: String(fd.get("email") || ""),
          city: String(fd.get("city") || ""),
          vehicle: String(fd.get("vehicle") || ""),
          rating,
          title: String(fd.get("title") || ""),
          body: String(fd.get("body") || ""),
        },
      });
      setDone(true);
      toast.success("Tack! Ditt omdöme granskas innan det publiceras.");
    } catch (err: any) {
      toast.error(err?.message || "Kunde inte skicka omdömet.");
    } finally {
      setSending(false);
    }
  }

  const jsonLd =
    count > 0 && average
      ? {
          "@context": "https://schema.org",
          "@type": "AutoDealer",
          name: "AK Husbil & Fritid",
          url: "https://www.akhusbilfritid.se",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Kalix",
            addressRegion: "Norrbotten",
            addressCountry: "SE",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: String(average),
            reviewCount: String(count),
            bestRating: "5",
            worstRating: "1",
          },
          review: reviews.slice(0, 10).map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.name },
            reviewRating: { "@type": "Rating", ratingValue: String(r.rating), bestRating: "5" },
            name: r.title || undefined,
            reviewBody: r.body,
            datePublished: r.approved_at ? r.approved_at.slice(0, 10) : undefined,
          })),
        }
      : null;

  return (
    <main className="pb-24">
      {jsonLd ? (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      ) : null}

      <section className="container-edge pt-10 md:pt-16">
        <p className="eyebrow">Omdömen</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight md:text-5xl">Vad kunderna säger</h1>
        <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
          Vi publicerar bara omdömen från riktiga kunder, och varje omdöme granskas manuellt innan
          det syns här. Inga köpta recensioner, inga påhittade betyg.
        </p>

        {count > 0 && average ? (
          <div className="mt-6 inline-flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3">
            <Stars value={Math.round(average)} size={18} />
            <span className="text-sm font-semibold">{average.toFixed(1)} av 5</span>
            <span className="text-sm text-muted-foreground">· {count} omdömen</span>
          </div>
        ) : null}
      </section>

      <section className="container-edge mt-10">
        {q.isLoading ? (
          <p className="text-sm text-muted-foreground">Hämtar omdömen…</p>
        ) : reviews.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border p-8 text-center">
            <ShieldCheck className="mx-auto text-accent" size={28} aria-hidden="true" />
            <p className="mt-3 font-medium">Inga omdömen publicerade ännu</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
              Vi har precis öppnat omdömessidan. Har du köpt av oss? Lämna gärna det första omdömet
              nedan – vi lägger inte upp något vi inte kan verifiera.
            </p>
          </div>
        ) : (
          <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {reviews.map((r) => (
              <li key={r.id} className="rounded-2xl border border-border bg-card p-5">
                <Stars value={r.rating} />
                {r.title ? <h2 className="mt-3 text-base font-semibold">{r.title}</h2> : null}
                <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{r.body}</p>
                <p className="mt-4 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{r.name}</span>
                  {r.city ? ` · ${r.city}` : ""}
                  {r.vehicle ? ` · ${r.vehicle}` : ""}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="container-edge mt-14 max-w-2xl">
        <form onSubmit={onSubmit} className="rounded-3xl border border-border bg-card p-5 md:p-7">
          <h2 className="text-xl font-semibold">Lämna ett omdöme</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Din e-post visas aldrig publikt – vi använder den bara för att kunna verifiera köpet.
          </p>

          {done ? (
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-accent/30 bg-accent/5 p-4">
              <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={20} aria-hidden="true" />
              <p className="text-sm">
                Tack! Omdömet ligger nu för granskning och publiceras normalt inom ett dygn.
              </p>
            </div>
          ) : (
            <div className="mt-5 grid gap-4">
              <fieldset>
                <legend className="text-sm font-medium">Betyg</legend>
                <div className="mt-2 flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setRating(i)}
                      aria-label={`${i} av 5`}
                      aria-pressed={rating === i}
                      className="flex h-11 w-11 items-center justify-center rounded-full transition hover:bg-muted"
                    >
                      <Star
                        size={22}
                        className={i <= rating ? "fill-accent text-accent" : "text-muted-foreground/40"}
                        aria-hidden="true"
                      />
                    </button>
                  ))}
                </div>
              </fieldset>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Namn" name="name" required autoComplete="name" />
                <Field label="E-post" name="email" type="email" required autoComplete="email" />
                <Field label="Ort" name="city" placeholder="Luleå" />
                <Field label="Fordon" name="vehicle" placeholder="Rapido C86, 2019" />
              </div>
              <Field label="Rubrik" name="title" placeholder="Trygg affär och ärlig genomgång" />
              <label className="block text-sm">
                <span className="font-medium">Ditt omdöme</span>
                <textarea
                  name="body"
                  rows={5}
                  required
                  minLength={20}
                  className="mt-1.5 w-full rounded-xl border border-border bg-background p-3 text-base"
                  placeholder="Berätta om köpet, bemötandet och fordonet…"
                />
              </label>
              <button
                type="submit"
                disabled={sending}
                className="min-h-[48px] rounded-full bg-foreground px-6 text-sm font-semibold text-background transition hover:opacity-90 disabled:opacity-60"
              >
                {sending ? "Skickar…" : "Skicka omdöme"}
              </button>
            </div>
          )}
        </form>
      </section>
    </main>
  );
}

function Field({
  label,
  name,
  type = "text",
  ...rest
}: { label: string; name: string; type?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block text-sm">
      <span className="font-medium">{label}</span>
      <input
        name={name}
        type={type}
        className="mt-1.5 min-h-[44px] w-full rounded-xl border border-border bg-background px-3 text-base"
        {...rest}
      />
    </label>
  );
}
